import { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';
import ParentLayout from '../components/ParentLayout';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { 
  Plus, X, Calendar, Clock, MapPin, Users, Settings, 
  CheckCircle, XCircle, AlertCircle, UserCheck, Edit, Trash2
} from 'lucide-react';
import { format } from 'date-fns';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ROLE_TYPES = [
  { value: 'game_day_setup', label: 'Game Day Setup' },
  { value: 'cleanup', label: 'Cleanup' },
  { value: 'snack_table', label: 'Snack Stand' },
  { value: 'chain_crew', label: 'Chain Crew' },
  { value: 'event_table', label: 'Event Table' },
  { value: 'fundraiser_support', label: 'Fundraiser Support' },
  { value: 'team_parent_support', label: 'Team Parent Support' },
  { value: 'equipment_day_support', label: 'Equipment Day' },
  { value: 'community_event_support', label: 'Community Event' },
  { value: 'registration_table', label: 'Registration Table' },
  { value: 'photography', label: 'Photography' },
  { value: 'general', label: 'General' }
];

const AdminVolunteerPage = () => {
  const { user } = useAuth();
  const [opportunities, setOpportunities] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [selectedOpp, setSelectedOpp] = useState(null);
  const [teams, setTeams] = useState([]);
  const [upcomingEvents, setUpcomingEvents] = useState([]);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    // Aug 2026 — multi-role: `volunteer_needs` is the source of truth
    // for role rows. Scalar `role_type` + `num_slots` are kept only so
    // any existing consumers that read them still resolve to the first
    // row; the submit handler consults `volunteer_needs` directly.
    volunteer_needs: [{ role_type: 'general', num_slots: 1 }],
    role_type: 'general',
    date: '',
    start_time: '',
    end_time: '',
    location: '',
    num_slots: 1,
    instructions: '',
    org_wide: true,
    team_id: '',
    event_id: ''
  });
  const [submitting, setSubmitting] = useState(false);
  // Post-submit warning when only some role POSTs succeed. Mirrors the
  // already-approved Event Creation partial-failure banner.
  const [volunteerWarning, setVolunteerWarning] = useState('');
  // Volunteer reminder composer modal state. Lives on top of the
  // existing DetailModal; opens when Send Reminder is clicked.
  const [reminderModal, setReminderModal] = useState({
    open: false,
    opp: null,
    recipientCount: 0,
    title: '',
    body: '',
    sending: false,
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [oppsRes, statsRes, teamsRes, eventsRes] = await Promise.all([
        axios.get(`${API_URL}/api/volunteer/opportunities?upcoming_only=false`, { withCredentials: true }),
        axios.get(`${API_URL}/api/volunteer/stats`, { withCredentials: true }),
        axios.get(`${API_URL}/api/teams`, { withCredentials: true }),
        axios.get(`${API_URL}/api/events?upcoming_only=true`, { withCredentials: true })
      ]);
      setOpportunities(oppsRes.data);
      setStats(statsRes.data);
      setTeams(teamsRes.data);
      // Filter to upcoming, non-canceled events for the linker dropdown.
      const events = Array.isArray(eventsRes.data) ? eventsRes.data : (eventsRes.data?.events || []);
      setUpcomingEvents(events.filter((ev) => ev.status !== 'canceled'));
    } catch (error) {
      console.error('Failed to fetch data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateOpportunity = async () => {
    if (!formData.title || !formData.date || !formData.start_time || !formData.end_time) {
      alert('Please fill in all required fields');
      return;
    }

    // Aug 2026 — multi-role fan-out. Merge duplicate role rows by
    // summing slot counts, then issue one POST per unique role. Uses
    // the exact same shape as the pre-existing endpoint.
    const rows = (formData.volunteer_needs || []).map(r => ({
      role_type: r?.role_type || 'general',
      num_slots: Math.max(1, parseInt(r?.num_slots, 10) || 1),
    }));
    if (rows.length === 0) {
      alert('Please add at least one volunteer role.');
      return;
    }
    const merged = new Map();
    for (const r of rows) {
      const prev = merged.get(r.role_type);
      merged.set(r.role_type, prev ? { ...r, num_slots: prev.num_slots + r.num_slots } : r);
    }
    const uniqueNeeds = Array.from(merged.values());

    setSubmitting(true);
    setVolunteerWarning('');
    const created = [];
    const failed = [];
    try {
      const commonPayload = {
        title: formData.title,
        description: formData.description,
        date: formData.date,
        start_time: formData.start_time,
        end_time: formData.end_time,
        location: formData.location,
        instructions: formData.instructions,
        org_wide: formData.org_wide,
        team_id: formData.org_wide ? null : formData.team_id || null,
        event_id: formData.event_id || null,
      };
      for (const need of uniqueNeeds) {
        const roleLabel = ROLE_TYPES.find(rt => rt.value === need.role_type)?.label || need.role_type;
        try {
          await axios.post(
            `${API_URL}/api/volunteer/opportunities`,
            { ...commonPayload, role_type: need.role_type, num_slots: need.num_slots },
            { withCredentials: true }
          );
          created.push(roleLabel);
        } catch (volErr) {
          const detailRaw = volErr?.response?.data?.detail;
          const detail = typeof detailRaw === 'string' ? detailRaw : 'Create failed';
          failed.push(`${roleLabel} (${need.num_slots}) — ${detail}`);
        }
      }
      if (failed.length === 0) {
        setShowCreateForm(false);
        setFormData({
          title: '',
          description: '',
          volunteer_needs: [{ role_type: 'general', num_slots: 1 }],
          role_type: 'general',
          date: '',
          start_time: '',
          end_time: '',
          location: '',
          num_slots: 1,
          instructions: '',
          org_wide: true,
          team_id: '',
          event_id: ''
        });
      } else {
        setVolunteerWarning(
          `${created.length} of ${uniqueNeeds.length} volunteer opportunities created. ` +
          (created.length ? `Created: ${created.join(', ')}. ` : '') +
          `Failed: ${failed.join('; ')}.`
        );
      }
      fetchData();
    } finally {
      setSubmitting(false);
    }
  };

  const handleCloseOpportunity = async (oppId) => {
    if (!window.confirm('Are you sure you want to close this opportunity?')) return;
    try {
      await axios.post(`${API_URL}/api/volunteer/opportunities/${oppId}/close`, {}, { withCredentials: true });
      fetchData();
      setSelectedOpp(null);
    } catch (error) {
      console.error('Failed to close:', error);
    }
  };

  // Build the default reminder body for the composer prefill. Mirrors
  // the backend default in routes_step7.py so what the admin sees is
  // exactly what would have been sent without the composer. Instructions
  // are appended on a new line when present (per task spec D).
  const buildDefaultReminder = (opp) => {
    const safeTitle = opp?.title || 'an upcoming opportunity';
    const title = `Volunteer Reminder: ${safeTitle}`;
    const dateLine = opp?.date || '';
    let timeLine = '';
    if (opp?.start_time && opp?.end_time) {
      timeLine = ` from ${opp.start_time} to ${opp.end_time}`;
    } else if (opp?.start_time) {
      timeLine = ` at ${opp.start_time}`;
    }
    const locationLine = opp?.location ? ` at ${opp.location}` : '';
    let body =
      `This is a friendly reminder that you're signed up to volunteer ` +
      `for ${safeTitle} on ${dateLine}${timeLine}${locationLine}. ` +
      `Thank you for stepping up!`;
    if (opp?.instructions) {
      body += `\n\nInstructions: ${opp.instructions}`;
    }
    return { title, body };
  };

  const handleSendReminder = (opp) => {
    // /api/volunteer/opportunities/{id} already filters canceled signups,
    // so signups.length is the active recipient count.
    const recipientCount = opp?.signups?.length || 0;
    const { title, body } = buildDefaultReminder(opp);
    setReminderModal({
      open: true,
      opp,
      recipientCount,
      title,
      body,
      sending: false,
    });
  };

  const submitReminder = async () => {
    const opp = reminderModal.opp;
    if (!opp) return;
    const trimmedTitle = reminderModal.title.trim();
    const trimmedBody = reminderModal.body.trim();
    // UI guard — backend still has its own fallback defaulting as a
    // safety net, but we block whitespace-only submits per spec.
    if (!trimmedTitle || !trimmedBody) return;
    setReminderModal((m) => ({ ...m, sending: true }));
    try {
      const res = await axios.post(
        `${API_URL}/api/volunteer/opportunities/${opp.opportunity_id}/send-reminder`,
        { title: trimmedTitle, body: trimmedBody },
        { withCredentials: true }
      );
      const count = res.data?.recipient_count ?? reminderModal.recipientCount;
      // Close composer, keep parent DetailModal open.
      setReminderModal({
        open: false,
        opp: null,
        recipientCount: 0,
        title: '',
        body: '',
        sending: false,
      });
      alert(`Reminder sent to ${count} volunteer${count === 1 ? '' : 's'}.`);
    } catch (error) {
      console.error('Failed to send reminder:', error);
      alert(error.response?.data?.detail || 'Failed to send reminder');
      setReminderModal((m) => ({ ...m, sending: false }));
    }
  };

  const closeReminderModal = () => {
    if (reminderModal.sending) return;
    setReminderModal({
      open: false,
      opp: null,
      recipientCount: 0,
      title: '',
      body: '',
      sending: false,
    });
  };

  const handleDeleteOpportunity = async (oppId) => {
    if (!window.confirm(
      'Permanently delete this opportunity? This cannot be undone.'
    )) return;
    try {
      await axios.delete(
        `${API_URL}/api/volunteer/opportunities/${oppId}`,
        { withCredentials: true }
      );
      setSelectedOpp(null);
      fetchData();
    } catch (error) {
      console.error('Failed to delete:', error);
      alert(error.response?.data?.detail || 'Failed to delete opportunity');
    }
  };

  const handleCompleteOpportunity = async (oppId) => {
    try {
      await axios.post(`${API_URL}/api/volunteer/opportunities/${oppId}/complete`, {}, { withCredentials: true });
      fetchData();
      setSelectedOpp(null);
    } catch (error) {
      console.error('Failed to complete:', error);
    }
  };

  const fetchOpportunityDetail = async (oppId) => {
    try {
      const res = await axios.get(`${API_URL}/api/volunteer/opportunities/${oppId}`, { withCredentials: true });
      setSelectedOpp(res.data);
    } catch (error) {
      console.error('Failed to fetch detail:', error);
    }
  };

  // Feb 2026 — Admin signup roster actions (PATCH /api/volunteer/admin/signups/{id}).
  // Handles status changes (confirmed / completed / no_show / canceled) and admin_note updates.
  // Refreshes the currently-open opportunity in place so the modal stays open.
  const handleAdminSignupUpdate = async (signupId, patch) => {
    try {
      await axios.patch(
        `${API_URL}/api/volunteer/admin/signups/${signupId}`,
        patch,
        { withCredentials: true }
      );
      // Refresh the modal opportunity so counts + roster reflect the change.
      if (selectedOpp?.opportunity_id) {
        await fetchOpportunityDetail(selectedOpp.opportunity_id);
      }
      // Also refresh the underlying list totals in the background.
      fetchData();
    } catch (error) {
      const detail = error?.response?.data?.detail || error?.message || 'Update failed';
      alert(`Could not update signup: ${detail}`);
      console.error('Admin signup update failed:', error);
    }
  };

  const handleAdminSignupNote = async (signup) => {
    const current = signup.admin_note || signup.notes || '';
    const next = window.prompt('Internal note for this signup (visible to admins only):', current);
    if (next === null) return; // cancelled
    await handleAdminSignupUpdate(signup.signup_id, { admin_note: next });
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'open':
        return <Badge className="bg-green-100 text-green-700">Open</Badge>;
      case 'full':
        return <Badge className="bg-blue-100 text-blue-700">Full</Badge>;
      case 'closed':
        return <Badge className="bg-gray-100 text-gray-700">Closed</Badge>;
      case 'completed':
        return <Badge className="bg-navy text-white">Completed</Badge>;
      case 'canceled':
        return <Badge className="bg-red-100 text-red-700">Canceled</Badge>;
      default:
        return <Badge variant="secondary">{status}</Badge>;
    }
  };

  const OpportunityRow = ({ opp }) => (
    <div 
      className="flex items-center justify-between p-4 bg-gray-50 rounded-lg mb-2 hover:bg-gray-100 transition-colors cursor-pointer"
      onClick={() => fetchOpportunityDetail(opp.opportunity_id)}
      data-testid={`opp-row-${opp.opportunity_id}`}
    >
      <div className="flex-1">
        <div className="flex items-center space-x-2 mb-1 flex-wrap">
          <h3 className="font-medium text-navy">{opp.title}</h3>
          {getStatusBadge(opp.status)}
          {opp.org_wide && <Badge variant="outline">Org-wide</Badge>}
          {opp.event_title && (
            <Badge
              variant="outline"
              className="border-navy text-navy"
              data-testid={`opp-linked-event-${opp.opportunity_id}`}
            >
              <Calendar className="w-3 h-3 mr-1" />
              Linked to: {opp.event_title}
            </Badge>
          )}
        </div>
        <div className="flex items-center space-x-4 text-sm text-gray-600">
          <span className="flex items-center">
            <Calendar className="w-3 h-3 mr-1" />
            {format(new Date(opp.date), 'MMM d, yyyy')}
          </span>
          <span className="flex items-center">
            <Clock className="w-3 h-3 mr-1" />
            {opp.start_time}
          </span>
          <span className="flex items-center">
            <Users className="w-3 h-3 mr-1" />
            {opp.filled_slots}/{opp.num_slots}
          </span>
        </div>
      </div>
      <div className="flex items-center space-x-2">
        {opp.status === 'open' && opp.available_slots === 0 && (
          <AlertCircle className="w-4 h-4 text-yellow-500" />
        )}
      </div>
    </div>
  );

  // NOTE (Feb 2026): originally this was `const CreateForm = () => (...)`
  // rendered as `<CreateForm />`. That is a *new component reference on
  // every render*, so React unmounts + remounts the whole subtree after
  // each keystroke — inputs lose focus and value resets after the first
  // character. The fix is to call this as a plain function that returns
  // JSX (`{renderCreateForm()}`), which lets React reconcile it as
  // regular children of AdminVolunteerPage without a new component
  // boundary.
  const renderCreateForm = () => (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="sticky top-0 bg-white border-b p-4 flex items-center justify-between">
          <h2 className="text-lg font-bold text-navy">Create Volunteer Opportunity</h2>
          <Button variant="ghost" size="sm" onClick={() => setShowCreateForm(false)}>
            <X className="w-5 h-5" />
          </Button>
        </div>

        <div className="p-4 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Title *</label>
            <Input
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              placeholder="e.g., Game Day Setup"
              data-testid="opp-title-input"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
            <Textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="What volunteers will do..."
              rows={3}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Volunteer Needs</label>
            <div className="space-y-2" data-testid="opp-volunteer-needs">
              {formData.volunteer_needs.map((row, idx) => (
                <div
                  key={idx}
                  className="grid grid-cols-[1fr,7rem,auto] gap-2 items-start"
                  data-testid={`opp-volunteer-need-row-${idx}`}
                >
                  <select
                    value={row.role_type}
                    onChange={(e) => setFormData(prev => {
                      const needs = prev.volunteer_needs.slice();
                      needs[idx] = { ...needs[idx], role_type: e.target.value };
                      return { ...prev, volunteer_needs: needs, role_type: needs[0].role_type };
                    })}
                    className="w-full border rounded-md px-3 py-2"
                    data-testid={`opp-volunteer-role-type-select-${idx}`}
                  >
                    {ROLE_TYPES.map((role) => (
                      <option key={role.value} value={role.value}>{role.label}</option>
                    ))}
                  </select>
                  <Input
                    type="number"
                    min="1"
                    value={row.num_slots}
                    onChange={(e) => setFormData(prev => {
                      const needs = prev.volunteer_needs.slice();
                      const n = Math.max(1, parseInt(e.target.value, 10) || 1);
                      needs[idx] = { ...needs[idx], num_slots: n };
                      return { ...prev, volunteer_needs: needs, num_slots: needs[0].num_slots };
                    })}
                    data-testid={`opp-volunteer-num-slots-input-${idx}`}
                  />
                  {idx === 0 ? (
                    <span aria-hidden="true" />
                  ) : (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setFormData(prev => {
                        const needs = prev.volunteer_needs.filter((_, i) => i !== idx);
                        return {
                          ...prev,
                          volunteer_needs: needs,
                          role_type: needs[0]?.role_type || 'general',
                          num_slots: needs[0]?.num_slots || 1,
                        };
                      })}
                      data-testid={`opp-volunteer-remove-row-${idx}`}
                    >
                      Remove
                    </Button>
                  )}
                </div>
              ))}
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => setFormData(prev => ({
                ...prev,
                volunteer_needs: [...prev.volunteer_needs, { role_type: 'general', num_slots: 1 }],
              }))}
              className="mt-2"
              data-testid="opp-volunteer-add-row-btn"
            >
              + Add Another Role
            </Button>
          </div>

          <div className="grid grid-cols-3 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Date *</label>
              <Input
                type="date"
                value={formData.date}
                onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                data-testid="opp-date-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Start *</label>
              <Input
                type="time"
                value={formData.start_time}
                onChange={(e) => setFormData({ ...formData, start_time: e.target.value })}
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">End *</label>
              <Input
                type="time"
                value={formData.end_time}
                onChange={(e) => setFormData({ ...formData, end_time: e.target.value })}
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Location</label>
            <Input
              value={formData.location}
              onChange={(e) => setFormData({ ...formData, location: e.target.value })}
              placeholder="e.g., Seaside Park"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Instructions for Volunteers</label>
            <Textarea
              value={formData.instructions}
              onChange={(e) => setFormData({ ...formData, instructions: e.target.value })}
              placeholder="What to bring, where to meet, etc."
              rows={2}
            />
          </div>

          <div>
            <label className="flex items-center space-x-2 cursor-pointer">
              <input
                type="checkbox"
                checked={formData.org_wide}
                onChange={(e) => setFormData({ ...formData, org_wide: e.target.checked })}
                className="rounded"
              />
              <span className="text-sm">Organization-wide (visible to all families)</span>
            </label>
          </div>

          {!formData.org_wide && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Team</label>
              <select
                value={formData.team_id}
                onChange={(e) => setFormData({ ...formData, team_id: e.target.value })}
                className="w-full border rounded-md px-3 py-2"
              >
                <option value="">Select a team...</option>
                {teams.map((team) => (
                  <option key={team.team_id} value={team.team_id}>{team.name}</option>
                ))}
              </select>
            </div>
          )}

          {/* Optional: link this opportunity to an existing event.
              Manual / optional — opportunities never auto-link, and
              an opportunity can stand alone without an event. */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Link to Event (optional)
            </label>
            <select
              value={formData.event_id}
              onChange={(e) => setFormData({ ...formData, event_id: e.target.value })}
              className="w-full border rounded-md px-3 py-2"
              data-testid="opp-event-link-select"
            >
              <option value="">Not linked to an event</option>
              {upcomingEvents.map((ev) => (
                <option key={ev.event_id} value={ev.event_id}>
                  {ev.title} — {ev.start_time
                    ? format(new Date(ev.start_time), 'MMM d, yyyy')
                    : ''}
                </option>
              ))}
            </select>
            <p className="text-xs text-gray-500 mt-1">
              Volunteers will see this opportunity on the event detail page.
            </p>
          </div>

          <div className="flex space-x-3 pt-4 border-t">
            <Button
              variant="outline"
              onClick={() => { setShowCreateForm(false); setVolunteerWarning(''); }}
              className="flex-1"
            >
              Cancel
            </Button>
            <Button
              onClick={handleCreateOpportunity}
              disabled={submitting}
              className="flex-1 bg-navy hover:bg-navy/90"
              data-testid="create-opp-btn"
            >
              {submitting ? 'Creating...' : 'Create Opportunity'}
            </Button>
          </div>
          {volunteerWarning && (
            <div
              className="mt-3 p-3 rounded-md border border-yellow-200 bg-yellow-50"
              data-testid="opp-volunteer-warning"
            >
              <p className="text-sm text-yellow-800">{volunteerWarning}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );

  const DetailModal = () => {
    if (!selectedOpp) return null;

    // /api/volunteer/opportunities/{id} already excludes canceled signups,
    // so signups.length is the count of active (non-canceled) volunteers.
    const activeSignupCount = selectedOpp.signups?.length || 0;
    const canDelete = activeSignupCount === 0;
    const isLifecycleOpen =
      selectedOpp.status === 'open' || selectedOpp.status === 'full';

    return (
      <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
          <div className="sticky top-0 bg-white border-b p-4 flex items-start justify-between gap-3">
            <h2 className="text-lg font-bold text-navy">Opportunity Details</h2>
            <div className="flex items-center flex-wrap gap-2 justify-end">
              {activeSignupCount > 0 && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleSendReminder(selectedOpp)}
                  className="text-navy border-navy hover:bg-navy/5"
                  data-testid="opp-send-reminder-btn"
                >
                  Send Reminder
                </Button>
              )}
              {isLifecycleOpen && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleCloseOpportunity(selectedOpp.opportunity_id)}
                  className="text-gray-700"
                  data-testid="opp-close-btn"
                >
                  Close
                </Button>
              )}
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleDeleteOpportunity(selectedOpp.opportunity_id)}
                disabled={!canDelete}
                title={
                  canDelete
                    ? 'Delete this opportunity'
                    : 'Cancel all signups before deleting'
                }
                className="text-red-600 border-red-200 hover:bg-red-50 disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="opp-delete-btn"
              >
                <Trash2 className="w-4 h-4 mr-1" /> Delete
              </Button>
              <Button variant="ghost" size="sm" onClick={() => setSelectedOpp(null)}>
                <X className="w-5 h-5" />
              </Button>
            </div>
          </div>

          <div className="p-4">
            <div className="flex items-center space-x-2 mb-4">
              {getStatusBadge(selectedOpp.status)}
              {selectedOpp.org_wide && <Badge variant="outline">Org-wide</Badge>}
            </div>

            <h3 className="text-xl font-bold text-navy mb-2">{selectedOpp.title}</h3>
            {selectedOpp.description && (
              <p className="text-gray-600 mb-4">{selectedOpp.description}</p>
            )}

            <div className="grid grid-cols-2 gap-4 mb-6">
              <div className="flex items-center space-x-2">
                <Calendar className="w-4 h-4 text-navy" />
                <span>{format(new Date(selectedOpp.date), 'EEEE, MMMM d, yyyy')}</span>
              </div>
              <div className="flex items-center space-x-2">
                <Clock className="w-4 h-4 text-navy" />
                <span>{selectedOpp.start_time} - {selectedOpp.end_time}</span>
              </div>
              {selectedOpp.location && (
                <div className="flex items-center space-x-2 col-span-2">
                  <MapPin className="w-4 h-4 text-navy" />
                  <span>{selectedOpp.location}</span>
                </div>
              )}
            </div>

            {selectedOpp.instructions && (
              <div className="bg-gray-50 rounded-lg p-3 mb-6">
                <p className="text-sm font-medium text-gray-700 mb-1">Instructions:</p>
                <p className="text-gray-600">{selectedOpp.instructions}</p>
              </div>
            )}

            {/* Signups */}
            <div className="border-t pt-4">
              <h4 className="font-semibold text-navy mb-3">
                Volunteer Roster ({selectedOpp.signup_count ?? selectedOpp.signups?.length ?? 0}/{selectedOpp.num_slots})
                {typeof selectedOpp.spots_remaining === 'number' && (
                  <span className="text-xs text-gray-500 ml-2">
                    ({selectedOpp.spots_remaining} spots remaining)
                  </span>
                )}
              </h4>

              {selectedOpp.signups?.length > 0 ? (
                <div className="overflow-x-auto">
                  <table className="min-w-full text-xs" data-testid="admin-roster-table">
                    <thead>
                      <tr className="bg-gray-50 text-gray-600">
                        <th className="px-2 py-1.5 text-left">Volunteer</th>
                        <th className="px-2 py-1.5 text-left">Role</th>
                        <th className="px-2 py-1.5 text-left">Signup Date</th>
                        <th className="px-2 py-1.5 text-left">Status</th>
                        <th className="px-2 py-1.5 text-left">Note</th>
                        <th className="px-2 py-1.5"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedOpp.signups.map((signup) => (
                        <tr
                          key={signup.signup_id}
                          className="border-t"
                          data-testid={`admin-roster-row-${signup.signup_id}`}
                        >
                          <td className="px-2 py-1.5 font-medium">
                            {signup.full_name || signup.display_name || 'Volunteer'}
                          </td>
                          <td className="px-2 py-1.5">{signup.role_title || selectedOpp.role_type || '—'}</td>
                          <td className="px-2 py-1.5">{(signup.signed_up_at || '').slice(0, 10)}</td>
                          <td className="px-2 py-1.5">
                            <Badge className={
                              signup.status === 'confirmed' ? 'bg-green-100 text-green-700' :
                              signup.status === 'completed' ? 'bg-navy text-white' :
                              signup.status === 'no_show' ? 'bg-red-100 text-red-700' :
                              signup.status === 'canceled' ? 'bg-gray-200 text-gray-600' :
                              'bg-gray-100'
                            }>
                              {signup.status}
                            </Badge>
                          </td>
                          <td className="px-2 py-1.5 text-gray-500">{signup.admin_note || signup.notes || '—'}</td>
                          <td className="px-2 py-1.5 text-right">
                            {signup.status !== 'canceled' && (
                              <div className="flex gap-1 justify-end">
                                {signup.status !== 'completed' && (
                                  <button
                                    onClick={() => handleAdminSignupUpdate(signup.signup_id, { status: 'completed' })}
                                    className="text-xs text-green-700 hover:underline"
                                    data-testid={`mark-completed-${signup.signup_id}`}
                                  >
                                    Complete
                                  </button>
                                )}
                                {signup.status !== 'no_show' && (
                                  <button
                                    onClick={() => handleAdminSignupUpdate(signup.signup_id, { status: 'no_show' })}
                                    className="text-xs text-orange-700 hover:underline"
                                  >
                                    No-show
                                  </button>
                                )}
                                <button
                                  onClick={() => handleAdminSignupNote(signup)}
                                  className="text-xs text-gray-600 hover:underline"
                                >
                                  Note
                                </button>
                                <button
                                  onClick={() => handleAdminSignupUpdate(signup.signup_id, { status: 'canceled' })}
                                  className="text-xs text-red-600 hover:underline"
                                  data-testid={`cancel-signup-${signup.signup_id}`}
                                >
                                  Cancel
                                </button>
                              </div>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              ) : (
                <p className="text-gray-500 text-center py-4">No signups yet</p>
              )}
            </div>

            {/* Actions */}
            {(selectedOpp.status === 'open' || selectedOpp.status === 'full') && (
              <div className="flex space-x-3 pt-4 mt-4 border-t">
                <Button
                  onClick={() => handleCompleteOpportunity(selectedOpp.opportunity_id)}
                  className="flex-1 bg-green-600 hover:bg-green-700"
                >
                  <CheckCircle className="w-4 h-4 mr-2" /> Mark Complete
                </Button>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <ParentLayout>
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="animate-pulse space-y-4">
            <div className="h-32 bg-gray-200 rounded-lg"></div>
            <div className="h-48 bg-gray-200 rounded-lg"></div>
          </div>
        </div>
      </ParentLayout>
    );
  }

  return (
    <ParentLayout>
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="admin-volunteer-page">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-3xl font-bold text-navy mb-2" style={{ fontFamily: 'Manrope, sans-serif' }}>
              Volunteer Management
            </h1>
            <p className="text-gray-600">Create and manage volunteer opportunities</p>
          </div>
          <Button
            onClick={() => setShowCreateForm(true)}
            className="bg-navy hover:bg-navy/90"
            data-testid="create-opportunity-btn"
          >
            <Plus className="w-4 h-4 mr-2" /> Create Opportunity
          </Button>
        </div>

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-6">
            <Card>
              <CardContent className="py-4 text-center">
                <p className="text-2xl font-bold text-navy">{stats.open_opportunities}</p>
                <p className="text-xs text-gray-600">Open</p>
              </CardContent>
            </Card>
            <Card className={stats.unfilled_slots > 10 ? 'border-yellow-400' : ''}>
              <CardContent className="py-4 text-center">
                <p className="text-2xl font-bold text-yellow-600">{stats.unfilled_slots}</p>
                <p className="text-xs text-gray-600">Unfilled Slots</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="py-4 text-center">
                <p className="text-2xl font-bold text-green-600">{stats.total_signups}</p>
                <p className="text-xs text-gray-600">Total Signups</p>
              </CardContent>
            </Card>
            <Card>
              <CardContent className="py-4 text-center">
                <p className="text-2xl font-bold text-light-blue">{stats.opportunities_this_week}</p>
                <p className="text-xs text-gray-600">This Week</p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Opportunities list */}
        <Card>
          <CardHeader>
            <CardTitle className="text-navy">All Opportunities</CardTitle>
          </CardHeader>
          <CardContent>
            {opportunities.length > 0 ? (
              opportunities.map((opp) => (
                <OpportunityRow key={opp.opportunity_id} opp={opp} />
              ))
            ) : (
              <div className="text-center py-8 text-gray-500">
                <Calendar className="w-12 h-12 mx-auto mb-3 opacity-50" />
                <p>No volunteer opportunities created yet</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Modals */}
        {showCreateForm && renderCreateForm()}
        <DetailModal />
        {reminderModal.open && (
          <div
            className="fixed inset-0 bg-black/60 z-[60] flex items-center justify-center p-4"
            data-testid="reminder-composer-modal"
          >
            <div className="bg-white rounded-xl max-w-lg w-full max-h-[90vh] overflow-y-auto shadow-2xl">
              <div className="sticky top-0 bg-white border-b p-4 flex items-center justify-between">
                <h2 className="text-lg font-bold text-navy">Send Volunteer Reminder</h2>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={closeReminderModal}
                  disabled={reminderModal.sending}
                  data-testid="reminder-close-x-btn"
                >
                  <X className="w-5 h-5" />
                </Button>
              </div>

              <div className="p-4 space-y-4">
                <div
                  className="rounded-md bg-navy/5 border border-navy/10 px-3 py-2 text-sm text-navy"
                  data-testid="reminder-recipient-count"
                >
                  This reminder will be sent to{' '}
                  <span className="font-semibold">
                    {reminderModal.recipientCount}
                  </span>{' '}
                  active volunteer
                  {reminderModal.recipientCount === 1 ? '' : 's'}.
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Title
                  </label>
                  <Input
                    value={reminderModal.title}
                    onChange={(e) =>
                      setReminderModal((m) => ({ ...m, title: e.target.value }))
                    }
                    placeholder="Reminder title"
                    disabled={reminderModal.sending}
                    data-testid="reminder-title-input"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Message
                  </label>
                  <Textarea
                    value={reminderModal.body}
                    onChange={(e) =>
                      setReminderModal((m) => ({ ...m, body: e.target.value }))
                    }
                    placeholder="Reminder message"
                    rows={6}
                    disabled={reminderModal.sending}
                    data-testid="reminder-body-input"
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    Pre-filled from the opportunity details. Edit freely before sending.
                  </p>
                </div>

                <div className="flex space-x-3 pt-2 border-t">
                  <Button
                    variant="outline"
                    onClick={closeReminderModal}
                    disabled={reminderModal.sending}
                    className="flex-1"
                    data-testid="reminder-cancel-btn"
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={submitReminder}
                    disabled={
                      reminderModal.sending ||
                      !reminderModal.title.trim() ||
                      !reminderModal.body.trim()
                    }
                    className="flex-1 bg-navy hover:bg-navy/90"
                    data-testid="reminder-confirm-send-btn"
                  >
                    {reminderModal.sending ? 'Sending…' : 'Send Reminder'}
                  </Button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </ParentLayout>
  );
};

export default AdminVolunteerPage;
